import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from './authConfig';

const msalInstance = new PublicClientApplication(msalConfig);

const isGuidLike = (value) => {
	if (!value || typeof value !== "string") return false;
	return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());
};

const isAuthorityLike = (value) => {
	if (!value || typeof value !== "string") return false;
	const normalized = value.trim();
	if (!normalized) return false;
	if (/^https:\/\/login\.microsoftonline\.com\/[a-zA-Z0-9._-]+\/?$/i.test(normalized)) {
		return true;
	}
	if (/^[a-zA-Z0-9._-]+$/.test(normalized)) {
		return true;
	}
	return false;
};

export const configureMsalClientId = (clientIdOverride) => {
	const normalized = typeof clientIdOverride === "string" ? clientIdOverride.trim() : "";
	if (!normalized || !isGuidLike(normalized)) {
		return { configured: false, reason: normalized ? "invalid" : "empty" };
	}

	if (msalInstance?.config?.auth?.clientId !== normalized) {
		msalInstance.config.auth.clientId = normalized;
	}

	// Persist per-app so redirect-back re-initializes MSAL with the correct clientId for each app
	try {
		const href = window.location.href;
		const stored = sessionStorage.getItem('preAuthUrl') || href;
		const url = (stored.includes('#code=') || stored.includes('?code=')) ? href : stored;
		const urlNormalized = url.replace('/fr/', '/').replace('/fr?', '?');
		const parts = urlNormalized.split('on-form');
		const appId = parts.length > 1 ? (parts[1].split('/').filter(s => s !== '')[0]?.split('?')[0]?.toLowerCase() || '') : '';
		const key = appId ? `msalClientId_${appId}` : 'msalClientId';
		sessionStorage.setItem(key, normalized);
	} catch {}

	return { configured: true, reason: "ok" };
};

const normalizeAuthority = (tenantOrAuthorityOverride) => {
	const normalized = typeof tenantOrAuthorityOverride === "string" ? tenantOrAuthorityOverride.trim() : "";
	if (!normalized) return null;
	if (!isAuthorityLike(normalized)) return null;
	if (normalized.startsWith("https://")) {
		return normalized.replace(/\/+$/, "");
	}
	return `https://login.microsoftonline.com/${normalized}`;
};

export const resolveMsalAuthority = (tenantOrAuthorityOverride) => normalizeAuthority(tenantOrAuthorityOverride);

export const getConfiguredMsalAuthority = () => {
	if (typeof window === "undefined") return null;
	const fromProgram = window?.programContainer?.UiAzureTenantId;
	if (typeof fromProgram === "string" && fromProgram.trim()) {
		return normalizeAuthority(fromProgram);
	}
	const fromSession = window?.sessionStorage?.getItem?.("uiAzureTenantId");
	if (typeof fromSession === "string" && fromSession.trim()) {
		return normalizeAuthority(fromSession);
	}
	return null;
};

export const configureMsalAuthority = (tenantOrAuthorityOverride) => {
	const normalizedAuthority = normalizeAuthority(tenantOrAuthorityOverride);
	if (!normalizedAuthority) {
		const normalizedInput = typeof tenantOrAuthorityOverride === "string" ? tenantOrAuthorityOverride.trim() : "";
		return { configured: false, reason: normalizedInput ? "invalid" : "empty" };
	}

	if (msalInstance?.config?.auth?.authority !== normalizedAuthority) {
		msalInstance.config.auth.authority = normalizedAuthority;
	}

	// Persist per-app so redirect-back re-initializes MSAL with the correct authority
	try {
		const href = window.location.href;
		const stored = sessionStorage.getItem('preAuthUrl') || href;
		const url = (stored.includes('#code=') || stored.includes('?code=')) ? href : stored;
		const urlNormalized = url.replace('/fr/', '/').replace('/fr?', '?');
		const parts = urlNormalized.split('on-form');
		const appId = parts.length > 1 ? (parts[1].split('/').filter(s => s !== '')[0]?.split('?')[0]?.toLowerCase() || '') : '';
		const key = appId ? `msalAuthority_${appId}` : 'msalAuthority';
		sessionStorage.setItem(key, normalizedAuthority);
	} catch {}

	return { configured: true, reason: "ok" };
};

export default msalInstance;